/**
 * Budget Type Definitions and Helpers
 * TypeScript-style interfaces documented in JSDoc for FinList Budget v2
 */

/**
 * @typedef {'MONTHLY'|'WEEKLY'|'CUSTOM'} BudgetCadence
 */

/**
 * @typedef {'INDIVIDUAL'|'HOUSEHOLD'} BudgetMode
 */

/**
 * @typedef {'ZERO_BASED'|'50_30_20'|'ESSENTIALS_FIRST'|'CUSTOM'} BudgetTemplate
 */

/**
 * @typedef {'OPEN'|'LOCKED'|'CLOSED'} BudgetStatus
 */

/**
 * @typedef {'CARRY'|'SAVE'|'RESET'} RolloverAction
 */

/**
 * @typedef {Object} RolloverRule
 * @property {RolloverAction} surplus - What to do with surplus
 * @property {RolloverAction} deficit - What to do with deficit
 */

/**
 * @typedef {Object} Budget
 * @property {string} id - Budget document ID
 * @property {string} name - Budget name
 * @property {BudgetMode} mode - Individual or household
 * @property {string} currency - Currency code (GBP, USD, etc.)
 * @property {BudgetCadence} cadence - Budget period type
 * @property {Date|import('firebase/firestore').Timestamp} startDate - Period start
 * @property {Date|import('firebase/firestore').Timestamp} endDate - Period end
 * @property {BudgetTemplate} template - Budget template used
 * @property {RolloverRule} rolloverRule - Rollover configuration
 * @property {BudgetStatus} status - Current status
 * @property {string[]|null} [members] - Member UIDs for household budgets
 * @property {Date|import('firebase/firestore').Timestamp} createdAt - Creation timestamp
 * @property {Date|import('firebase/firestore').Timestamp} updatedAt - Last update timestamp
 * @property {string} [month] - Legacy month key (YYYY-MM)
 * @property {string} [periodType] - Legacy period type
 */

/**
 * @typedef {Object} BudgetLine
 * @property {string} id - Line document ID
 * @property {string} categoryId - Category ID from taxonomy
 * @property {number} planned - Planned/budgeted amount
 * @property {number} actual - Actual spending (computed)
 * @property {number} refunds - Refunds received (computed)
 * @property {number} remaining - Remaining budget (computed)
 * @property {number} carryIn - Carried from previous period
 * @property {number} carryOut - To carry to next period
 * @property {GoalLink|null} [goalLink] - Link to associated goal
 * @property {string} [notes] - Optional notes
 * @property {Date|import('firebase/firestore').Timestamp} createdAt - Creation timestamp
 * @property {Date|import('firebase/firestore').Timestamp} updatedAt - Last update timestamp
 */

/**
 * @typedef {Object} GoalLink
 * @property {string} goalId - Linked goal ID
 * @property {'SAVE'|'SPEND_CAP'|'PAY_OFF'|'INVEST'} intent - Goal intent type
 */

/**
 * @typedef {Object} BudgetSummary
 * @property {number} incomePlanned - Planned income
 * @property {number} incomeActual - Actual income
 * @property {number} expensePlanned - Planned expenses
 * @property {number} expenseActual - Actual expenses
 * @property {number} safeToSpend - Safe-to-spend amount
 * @property {number} burnRatePct - Burn rate percentage
 * @property {number} projectedEOM - Projected end-of-month spending
 * @property {BudgetAlert[]} alerts - Active alerts
 * @property {Date|import('firebase/firestore').Timestamp} lastCalculated - Last calculation time
 */

/**
 * @typedef {Object} BudgetAlert
 * @property {string} type - Alert type identifier
 * @property {string} message - Human-readable message
 * @property {string} [categoryId] - Related category ID
 * @property {'info'|'warn'|'crit'} severity - Alert severity level
 */

/**
 * Calculate proration for mid-period budget
 * @param {number} monthlyPlanned - Full month planned amount
 * @param {Date} startDate - Actual start date
 * @param {Date} endDate - Period end date
 * @returns {number} Prorated amount
 */
export function calculateProration(monthlyPlanned, startDate, endDate) {
  const totalDays = Math.ceil((endDate - startDate) / (1000 * 60 * 60 * 24)) + 1;
  const monthStart = new Date(startDate.getFullYear(), startDate.getMonth(), 1);
  const monthEnd = new Date(startDate.getFullYear(), startDate.getMonth() + 1, 0);
  const totalMonthDays = Math.ceil((monthEnd - monthStart) / (1000 * 60 * 60 * 24)) + 1;
  
  return (monthlyPlanned * totalDays) / totalMonthDays;
}

/**
 * Calculate burn rate percentage
 * @param {number} actualSpent - Amount spent so far
 * @param {number} plannedTotal - Total planned for period
 * @param {Date} startDate - Period start
 * @param {Date} now - Current date
 * @returns {number} Burn rate percentage
 */
export function calculateBurnRate(actualSpent, plannedTotal, startDate, now) {
  if (plannedTotal === 0) return 0;
  
  const elapsed = Math.max(0, now - startDate);
  if (elapsed === 0) return 0;
  
  const totalPeriod = 30 * 24 * 60 * 60 * 1000; // Assume 30 days
  const elapsedRatio = Math.min(1, elapsed / totalPeriod);
  
  if (elapsedRatio === 0) return 0;
  
  return (actualSpent / (plannedTotal * elapsedRatio)) * 100;
}

/**
 * Project end-of-month spending
 * @param {number} actualSpent - Amount spent so far
 * @param {number} elapsedRatio - Ratio of period elapsed (0-1)
 * @returns {number} Projected total spending
 */
export function projectEndOfMonth(actualSpent, elapsedRatio) {
  if (elapsedRatio === 0 || elapsedRatio >= 1) return actualSpent;
  return actualSpent / elapsedRatio;
}

/**
 * Calculate safe-to-spend amount
 * @param {number} income - Available income (actual or planned)
 * @param {number} committed - Committed expenses (bills, etc.)
 * @param {number} actualSpent - Already spent amount
 * @returns {number} Safe-to-spend amount
 */
export function calculateSafeToSpend(income, committed, actualSpent) {
  return income - committed - actualSpent;
}

/**
 * Apply rollover rules to calculate carry amounts
 * @param {number} remaining - Remaining budget amount
 * @param {RolloverRule} rules - Rollover rules
 * @param {boolean} isSurplus - True if surplus, false if deficit
 * @returns {number} Amount to carry forward
 */
export function applyRollover(remaining, rules, isSurplus) {
  if (isSurplus) {
    switch (rules.surplus) {
      case 'CARRY':
        return remaining;
      case 'SAVE':
        // Would move to savings goal
        return 0;
      case 'RESET':
        return 0;
      default:
        return 0;
    }
  } else {
    // Deficit
    switch (rules.deficit) {
      case 'CARRY':
        return remaining; // Negative value
      case 'RESET':
        return 0;
      default:
        return 0;
    }
  }
}

/**
 * Generate budget template allocations
 * @param {BudgetTemplate} template - Template type
 * @param {number} totalIncome - Total available income
 * @param {Array} categories - Available categories
 * @returns {Object} Category allocations {categoryId: amount}
 */
export function generateTemplateAllocations(template, totalIncome, categories) {
  const allocations = {};
  
  switch (template) {
    case '50_30_20': {
      // 50% Needs, 30% Wants, 20% Savings
      const needs = totalIncome * 0.5;
      const wants = totalIncome * 0.3;
      const savings = totalIncome * 0.2;
      
      // Distribute among categories (simplified)
      const essentialCategories = categories.filter(c => c.isEssential);
      const nonEssentialCategories = categories.filter(c => !c.isEssential && c.type === 'expense');
      const savingsCategories = categories.filter(c => c.type === 'savings');
      
      const essentialAmount = essentialCategories.length > 0 ? needs / essentialCategories.length : 0;
      const wantAmount = nonEssentialCategories.length > 0 ? wants / nonEssentialCategories.length : 0;
      const savingsAmount = savingsCategories.length > 0 ? savings / savingsCategories.length : 0;
      
      essentialCategories.forEach(cat => allocations[cat.id] = essentialAmount);
      nonEssentialCategories.forEach(cat => allocations[cat.id] = wantAmount);
      savingsCategories.forEach(cat => allocations[cat.id] = savingsAmount);
      break;
    }
    
    case 'ESSENTIALS_FIRST': {
      // Allocate to essentials first, then distribute remainder
      const essentialCategories = categories.filter(c => c.isEssential);
      const totalEssentialBudget = essentialCategories.reduce((sum, cat) => sum + (cat.defaultBudget || 0), 0);
      
      essentialCategories.forEach(cat => {
        allocations[cat.id] = cat.defaultBudget || 0;
      });
      
      const remaining = totalIncome - totalEssentialBudget;
      const nonEssentialCategories = categories.filter(c => !c.isEssential);
      const perCategory = nonEssentialCategories.length > 0 ? remaining / nonEssentialCategories.length : 0;
      
      nonEssentialCategories.forEach(cat => {
        allocations[cat.id] = Math.max(0, perCategory);
      });
      break;
    }
    
    case 'ZERO_BASED': {
      // All income allocated, user must assign manually
      // Just use default budgets as starting point
      categories.forEach(cat => {
        allocations[cat.id] = cat.defaultBudget || 0;
      });
      break;
    }
    
    default:
      // CUSTOM - no preset allocations
      break;
  }
  
  return allocations;
}

/**
 * Format currency amount - Re-export from global utils
 * This ensures BudgetPage uses the same formatter as the rest of the app
 */
import { formatCurrency as globalFormatCurrency } from '@/lib/utils';
import { getCurrencySymbol as globalGetCurrencySymbol } from '@/utils/currencyUtils';
export const formatCurrency = globalFormatCurrency;
export const getCurrencySymbol = globalGetCurrencySymbol;

/**
 * Get severity color for alerts
 * @param {'info'|'warn'|'crit'} severity 
 * @returns {string} Tailwind color class
 */
export function getAlertColor(severity) {
  switch (severity) {
    case 'info':
      return 'bg-blue-50 text-blue-700 border-blue-200 dark:bg-slate-900 dark:text-blue-200 dark:border-slate-700';
    case 'warn':
      return 'bg-amber-50 text-amber-700 border-amber-200 dark:bg-slate-900 dark:text-amber-200 dark:border-slate-700';
    case 'crit':
      return 'bg-red-50 text-red-700 border-red-200 dark:bg-slate-900 dark:text-red-200 dark:border-slate-700';
    default:
      return 'bg-gray-50 text-gray-700 border-gray-200 dark:bg-slate-900 dark:text-gray-200 dark:border-slate-700';
  }
}

/**
 * Calculate line remaining amount
 * @param {BudgetLine} line - Budget line
 * @returns {number} Remaining amount
 */
export function calculateLineRemaining(line) {
  const planned = Number(line.planned) || 0;
  const actual = Number(line.actual) || 0;
  const refunds = Number(line.refunds) || 0;
  const carryIn = Number(line.carryIn) || 0;
  
  return planned - (actual - refunds) + carryIn;
}

/**
 * Get progress percentage for a budget line
 * @param {BudgetLine} line - Budget line
 * @returns {number} Progress percentage (0-100+)
 */
export function getLineProgress(line) {
  const planned = Number(line.planned) || 0;
  if (planned === 0) return 0;
  
  const actual = Number(line.actual) || 0;
  const refunds = Number(line.refunds) || 0;
  
  return ((actual - refunds) / planned) * 100;
}

/**
 * Get progress color for budget line
 * @param {number} percentage - Progress percentage
 * @returns {string} Tailwind color class
 */
export function getProgressColor(percentage) {
  if (percentage < 50) return 'bg-green-500';
  if (percentage < 75) return 'bg-yellow-500';
  if (percentage < 90) return 'bg-orange-500';
  return 'bg-red-500';
}
